
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { updateClientRequestStatus } from '@/services/admin';
import { useClientFiltering } from './useClientFiltering';

/**
 * Hook for handling client status updates
 */
export const useClientStatus = (
  onboardingRequests: any[],
  setOnboardingRequests: React.Dispatch<React.SetStateAction<any[]>>,
  setActiveTab: (tab: string) => void,
  refreshRequests: () => Promise<void>
) => {
  const { toast } = useToast();
  const { denormalizeStatus } = useClientFiltering();
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const handleRequestAction = (request: any) => {
    setSelectedRequest(request);
    // Set initial status value based on current normalized status
    const currentStatus = request.status === 'pending_verification' ? 'pending' : 
                         request.status === 'verified' ? 'approved' : request.status;
    setNewStatus(currentStatus);
    setIsDialogOpen(true);
  };

  const updateRequestStatus = async () => {
    if (!selectedRequest || !newStatus) return;

    setIsProcessing(true);
    
    try {
      console.log(`Updating request status: ${selectedRequest.id} to ${newStatus}`);
      
      const { success, updatedRecord, error } = await updateClientRequestStatus(
        selectedRequest.id,
        newStatus // This will be normalized in the service
      );

      if (!success) throw error;
      
      if (!updatedRecord) {
        throw new Error("No updated record returned from server");
      }
      
      console.log("Status update successful, updated record:", updatedRecord);
      
      // Close the dialog first
      setIsDialogOpen(false);
      
      // Update the client lists right away with the new status
      setOnboardingRequests((prevRequests) => {
        const updatedRequests = prevRequests.map(req => 
          req.id === selectedRequest.id ? { ...req, status: denormalizeStatus(newStatus) } : req
        );
        
        console.log("Updated onboarding requests:", updatedRequests);
        return updatedRequests;
      });
      
      // Show success message
      toast({
        title: "Status Updated",
        description: `Request for ${selectedRequest.company_name} has been updated to ${newStatus}.`,
      });
      
      // Determine which tab to switch to based on new status
      if (newStatus === 'approved') {
        setActiveTab('verified-clients');
      } else if (newStatus === 'rejected') {
        setActiveTab('rejected-clients');
      } else {
        setActiveTab('client-requests');
      }
      
      // Refresh the data to ensure consistency
      refreshRequests();
      
    } catch (error: any) {
      console.error('Error updating request status:', error);
      toast({
        title: "Error",
        description: `Failed to update request status: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
      // Keep the dialog open on error so the user can try again
      setIsDialogOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    selectedRequest,
    isDialogOpen,
    setIsDialogOpen,
    newStatus,
    setNewStatus,
    isProcessing,
    handleRequestAction,
    updateRequestStatus
  };
};
